import React, { useState, useEffect } from 'react';
import { getFollowing, uploadProfilePic, updateBio } from '../photoFunctions';

// ==================== 1. MELLOW GLASS DESIGN TOKENS ====================
// The "Vision" Palette: Softer, warmer, less contrasty.
const COLORS = {
  // Text: Softened from harsh black/brown to deep espresso & taupe
  textMain: '#4A3B2A', 
  textSec: '#8C7B68',  
  
  // Accents: Mellow Gold/Sand rather than yellow-gold
  accentPrimary: '#C2A878', 
  accentSecondary: '#D4C5A8',
  
  // The Material
  glassSurface: 'rgba(245, 242, 237, 0.65)', // Warm milky white
  glassBorder: 'rgba(255, 255, 255, 0.4)',   // Subtle edge light
  glassHighlight: 'rgba(255, 255, 255, 0.8)',
  
  // Shadows: Diffused ambient occlusion, not hard drops
  shadowDiffuse: '0 20px 50px -12px rgba(60, 45, 30, 0.15)',
  shadowInner: 'inset 0 0 0 1px rgba(255,255,255,0.3)'
};

const FONTS = {
  serif: "-apple-system-ui-serif, 'Iowan Old Style', 'Apple Garamond', serif", // Iowan is very "Apple Book"
  sans: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
};

// ==================== USER PROFILE DROPDOWN ====================
export const UserProfile = ({ user, profile, onClose, onAccountDeleted }) => {
  const [following, setFollowing] = useState([]);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState(profile?.bio || '');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) loadFollowing();
  }, [user]);

  useEffect(() => {
    setBioText(profile?.bio || '');
  }, [profile]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const loadFollowing = async () => {
    try {
      const followingData = await getFollowing(user.id);
      setFollowing(followingData);
    } catch (error) {
      console.error('Error loading following:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadProfilePic(user.id, file);
      window.location.reload();
    } catch (error) {
      console.error('Profile pic upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleBioSave = async () => {
    try {
      await updateBio(user.id, bioText);
      setIsEditingBio(false);
      window.location.reload();
    } catch (error) {
      console.error('Bio update failed:', error);
      alert('Update failed. Please try again.');
    }
  };

  return (
    <>
      <div style={styles.backdrop} onClick={onClose} />
      
      <div style={styles.panel}>
        {/* Lighting effect: Top spotlight */}
        <div style={styles.ambientLight}></div>

        <div style={styles.dragHandleContainer}>
            <div style={styles.dragHandle}></div>
        </div>

        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Profile</h2>
          <button onClick={onClose} className="ios-active-scale" style={styles.closeButton}>
            <span style={{marginTop: '-2px'}}>✕</span>
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.avatarContainer}>
            <div style={styles.avatarWrapper}>
              <div style={{
                ...styles.avatar,
                background: profile?.avatar ? `url(${profile.avatar}) center/cover` : COLORS.glassSurface
              }}>
                {!profile?.avatar && (profile?.username?.[0] || 'U').toUpperCase()}
              </div>
              
              <label style={{
                ...styles.uploadBtn,
                cursor: uploading ? 'not-allowed' : 'pointer',
              }}>
                <span style={styles.uploadIcon}>{uploading ? '...' : '+'}</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment" 
                  onChange={handleProfilePicUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            <h3 style={styles.username}>{profile?.username || 'Loading...'}</h3>
            <p style={styles.fullName}>{profile?.name || ''}</p>
          </div>

          {/* Platter 1: Bio */}
          <div style={styles.platter}>
            <div style={styles.sectionHeader}>
              <h4 style={styles.sectionTitle}>About</h4>
              {!isEditingBio ? (
                <button onClick={() => setIsEditingBio(true)} className="ios-active-scale" style={styles.actionLink}>
                  Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => { setIsEditingBio(false); setBioText(profile?.bio || ''); }} style={styles.cancelLink}>
                    Cancel
                  </button>
                  <button onClick={handleBioSave} style={styles.actionLink}>Done</button>
                </div>
              )}
            </div>
            
            {isEditingBio ? (
              <textarea
                value={bioText}
                onChange={(e) => setBioText(e.target.value)}
                placeholder="Write a short bio..."
                maxLength={140}
                style={styles.textarea}
                autoFocus
              />
            ) : (
              <p style={{
                ...styles.bioText,
                fontStyle: bioText ? 'normal' : 'italic',
                opacity: bioText ? 1 : 0.6,
              }}>
                {bioText || 'No bio yet.'}
              </p>
            )}
          </div>

          {/* Platter 2: Following */}
          <div style={styles.platter}>
            <h4 style={{...styles.sectionTitle, marginBottom: '12px'}}>Following • {following.length}</h4>
            
            {loading ? (
              <p style={styles.emptyState}>Loading...</p>
            ) : following.length === 0 ? (
              <p style={styles.emptyState}>Not following anyone</p>
            ) : (
              <div style={styles.followingList}>
                {following.map((user) => (
                  <div key={user.id} className="ios-list-item" style={styles.userRow}>
                    <div style={{
                      ...styles.smallAvatar,
                      background: user.avatar ? `url(${user.avatar}) center/cover` : '#EAE5DD'
                    }}>
                      {!user.avatar && (user.username?.[0] || 'U').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <p style={styles.cardUsername}>{user.username}</p>
                      {user.bio && <p style={styles.cardBio}>{user.bio}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes softPop {
          from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        ::-webkit-scrollbar { width: 0px; background: transparent; }
        
        .ios-active-scale { transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.2s; }
        .ios-active-scale:active { transform: scale(0.97); opacity: 0.8; }
        
        .ios-list-item { transition: background 0.2s ease; border-radius: 12px; }
        .ios-list-item:active { background: rgba(0,0,0,0.04) !important; }
        
        textarea::placeholder { color: ${COLORS.textSec}; opacity: 0.5; }
      `}</style>
    </>
  );
};

// ==================== PROFILE BUTTON ====================
export const UserProfileButton = ({ profile, onClick, signOut, onNavigateToSettings, navigateMode = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.user-profile-button-container')) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const handleAction = (action) => {
    setShowDropdown(false);
    if (action) action();
  };

  return (
    <>
      <div className="user-profile-button-container" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', zIndex: 10 }}>
        {/* Changed from "Pill" to "Floating Glass" */}
        <div style={styles.glassButtonContainer}>
            <button
              onClick={() => navigateMode ? onClick() : setShowDropdown(!showDropdown)}
              className="ios-active-scale"
              style={{
                ...styles.mainButton,
                paddingRight: navigateMode ? '12px' : '16px',
              }}
            >
              Profile
            </button>
            
            {navigateMode && signOut && (
              <>
                <div style={styles.verticalDivider}></div>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}
                  className="ios-active-scale"
                  style={styles.dropdownToggle}
                  aria-label="More options"
                >
                  <span style={{fontSize: '10px', opacity: 0.6}}>▼</span>
                </button>
              </>
            )}
        </div>
        
        {showDropdown && (
          <div style={styles.dropdownMenu}>
            {!navigateMode && (
              <button onClick={() => handleAction(onClick)} className="ios-list-item" style={styles.menuItemBordered}>
                View Profile
              </button>
            )}
            {onNavigateToSettings && (
              <button onClick={() => handleAction(onNavigateToSettings)} className="ios-list-item" style={styles.menuItemBordered}>
                Settings
              </button>
            )}
            <button onClick={() => handleAction(signOut)} className="ios-list-item" style={{...styles.menuItem, color: '#FF3B30'}}>
              Log Out
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ==================== REFINED STYLES ====================
const styles = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    background: 'rgba(50, 40, 30, 0.15)', // Warm dim
    backdropFilter: 'blur(4px)',
    zIndex: 4999, WebkitTapHighlightColor: 'transparent'
  },
  panel: {
    position: 'fixed',
    top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 32px)', 
    maxWidth: '360px',
    maxHeight: '80vh',
    
    // THE NEW GLASS:
    // 1. Very translucent warm white
    // 2. High blur for that "thick glass" feel
    // 3. Inner shadow for edge definition instead of thick borders
    backgroundColor: COLORS.glassSurface,
    backdropFilter: 'blur(50px) saturate(180%)',
    WebkitBackdropFilter: 'blur(50px) saturate(180%)',
    
    borderRadius: '36px', // Continuous curve (Squircle-ish)
    border: `1px solid ${COLORS.glassBorder}`,
    boxShadow: COLORS.shadowDiffuse,
    
    zIndex: 5000, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    animation: 'softPop 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
  },
  ambientLight: {
    position: 'absolute', top: 0, left: 0, right: 0, height: '150px',
    background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
    pointerEvents: 'none', opacity: 0.6
  },
  dragHandleContainer: {
    width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: '6px',
    position: 'relative', zIndex: 2
  },
  dragHandle: {
    width: '36px', height: '4px', background: 'rgba(0,0,0,0.1)', borderRadius: '10px'
  },
  header: {
    padding: '8px 24px 12px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'relative', zIndex: 2
  },
  headerTitle: {
    fontFamily: FONTS.serif, fontSize: '22px',
    color: COLORS.textMain, fontWeight: 700, margin: 0,
    letterSpacing: '-0.02em'
  },
  closeButton: {
    background: 'rgba(0,0,0,0.05)', 
    borderRadius: '50%', border: 'none',
    width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: COLORS.textSec, fontSize: '14px', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent'
  },
  content: {
    padding: '0 20px 32px 20px', overflowY: 'auto', flex: 1,
    WebkitOverflowScrolling: 'touch',
    display: 'flex', flexDirection: 'column', gap: '16px',
    position: 'relative', zIndex: 2
  },
  avatarContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '8px 0 16px 0'
  },
  avatarWrapper: { position: 'relative', marginBottom: '12px' },
  avatar: {
    width: '90px', height: '90px', borderRadius: '50%',
    // Soft glow instead of hard border
    boxShadow: '0 8px 20px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(255,255,255,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '32px', color: COLORS.textMain,
    fontFamily: FONTS.serif, fontWeight: 500,
    backgroundSize: 'cover', backgroundPosition: 'center'
  },
  uploadBtn: {
    position: 'absolute', bottom: '0', right: '0',
    width: '28px', height: '28px', borderRadius: '50%',
    background: COLORS.textMain, 
    color: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: '2px solid #fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent'
  },
  uploadIcon: { fontSize: '16px', fontWeight: 300, marginTop: '-2px' },
  username: {
    fontFamily: FONTS.serif, fontSize: '26px',
    color: COLORS.textMain, fontWeight: 500, margin: '0 0 4px 0',
    letterSpacing: '-0.02em'
  },
  fullName: {
    fontFamily: FONTS.sans, fontSize: '14px',
    color: COLORS.textSec, fontWeight: 400, margin: 0,
  },
  // PLATTERS: The new "Bubble"
  platter: {
    background: 'rgba(255, 255, 255, 0.4)', // Very subtle
    borderRadius: '24px',
    padding: '20px',
    // Blending the platter into the glass
    boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.4)',
  },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  sectionTitle: {
    fontFamily: FONTS.sans, fontSize: '11px',
    color: COLORS.textSec, textTransform: 'uppercase', letterSpacing: '0.06em',
    fontWeight: 700, margin: 0, opacity: 0.8
  },
  actionLink: {
    background: 'none', border: 'none', color: COLORS.accentPrimary,
    cursor: 'pointer', fontFamily: FONTS.sans, fontSize: '13px',
    fontWeight: 600, letterSpacing: '-0.01em', padding: '4px 8px', margin: '-4px -8px',
    WebkitTapHighlightColor: 'transparent'
  },
  cancelLink: {
    background: 'none', border: 'none', color: COLORS.textSec,
    cursor: 'pointer', fontFamily: FONTS.sans, fontSize: '14px',
    fontWeight: 400, padding: '4px',
  },
  textarea: {
    width: '100%', minHeight: '70px', padding: '0', margin: '8px 0 0 0',
    border: 'none', background: 'transparent',
    fontFamily: FONTS.sans, fontSize: '16px', lineHeight: '1.5',
    color: COLORS.textMain, outline: 'none', resize: 'none',
    WebkitTapHighlightColor: 'transparent'
  },
  bioText: {
    fontFamily: FONTS.sans, fontSize: '15px',
    color: COLORS.textMain, lineHeight: 1.5, fontWeight: 400,
    letterSpacing: '0em', margin: 0, wordBreak: 'break-word'
  },
  emptyState: {
    fontFamily: FONTS.sans, fontSize: '14px', color: COLORS.textSec,
    textAlign: 'center', padding: '12px 0', opacity: 0.7
  },
  followingList: {
    display: 'flex', flexDirection: 'column', gap: '4px',
    maxHeight: '180px', overflowY: 'auto',
    marginTop: '8px'
  },
  userRow: {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '8px',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent'
  },
  smallAvatar: {
    width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px',
    color: COLORS.textMain, fontFamily: FONTS.serif, fontWeight: 600,
    background: '#eee', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
  },
  cardUsername: {
    fontFamily: FONTS.sans, fontSize: '15px',
    color: COLORS.textMain, fontWeight: 500, margin: 0, letterSpacing: '-0.01em'
  },
  cardBio: {
    fontFamily: FONTS.sans, fontSize: '12px',
    color: COLORS.textSec, margin: 0,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
  },
  
  // === FLOATING BUTTON STYLES ===
  glassButtonContainer: {
    display: 'inline-flex',
    // Mellow glass button
    background: 'rgba(255, 255, 255, 0.65)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderRadius: '30px',
    padding: '3px',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
  },
  mainButton: {
    padding: '0 12px',
    background: 'transparent', color: COLORS.textMain, border: 'none',
    fontFamily: FONTS.sans, fontSize: '13px',
    letterSpacing: '-0.01em', cursor: 'pointer',
    fontWeight: 600, minHeight: '36px',
    WebkitTapHighlightColor: 'transparent', whiteSpace: 'nowrap'
  },
  verticalDivider: {
    width: '1px', height: '16px', background: 'rgba(0,0,0,0.1)', alignSelf: 'center'
  },
  dropdownToggle: {
    background: 'transparent', border: 'none', color: COLORS.textMain,
    cursor: 'pointer', padding: '0 10px', minHeight: '36px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent'
  },
  dropdownMenu: {
    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
    background: 'rgba(255, 255, 255, 0.9)', 
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    border: 'none',
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
    borderRadius: '16px', minWidth: '180px', overflow: 'hidden',
    padding: '6px',
    animation: 'softPop 0.2s cubic-bezier(0.2, 0.8, 0.2, 1)'
  },
  menuItem: {
    padding: '10px 12px', borderRadius: '10px',
    fontFamily: FONTS.sans, fontSize: '14px', fontWeight: 500,
    color: COLORS.textMain, cursor: 'pointer',
    border: 'none', background: 'none', width: '100%', textAlign: 'left',
    minHeight: '40px',
    WebkitTapHighlightColor: 'transparent', display: 'flex', alignItems: 'center'
  },
  menuItemBordered: {
    padding: '10px 12px', borderRadius: '10px',
    fontFamily: FONTS.sans, fontSize: '14px', fontWeight: 500,
    color: COLORS.textMain, cursor: 'pointer',
    border: 'none',
    background: 'none', width: '100%', textAlign: 'left',
    minHeight: '40px', marginBottom: '2px',
    WebkitTapHighlightColor: 'transparent', display: 'flex', alignItems: 'center'
  }
};